/**
 * @file LanguageSelectorContainer — the one language-picker behaviour.
 * @description Owns open/closed state (controlled or uncontrolled), the
 * width-derived presentation switch, and — the point of SE-6508 — THE ONE
 * LABEL RULE that every language surface now shares.
 *
 * ## The one label rule
 *
 * A language is named by its display label from `@/constants/locales`, i.e.
 * the label of the OFFERED locale the app will actually act in
 * ({@link getLanguageLabel}). No surface renders a raw locale code, and no
 * surface names a language for a value it has not resolved.
 *
 * Before this, two rules were live. The AI-summary picker labelled by a `find`
 * over the six with a raw-code fallback, so a user stranded on one of the
 * fourteen locales LPW-1.0 dropped read `Generate · ja_JP`; Settings resolved
 * the same stored value to "English". SE-6506 preserved the raw-code fallback
 * as *self-consistent* — label and generated output agreed. That is no longer
 * true: since SE-6505 the prompt builders themselves call `getLanguageLabel`,
 * so a `ja_JP` profile already generates ENGLISH. The raw-code label had
 * become the lie, describing a generation that no longer happens, so the
 * resolved label is the rule that survives.
 *
 * ## Three states, kept apart
 *
 * Resolving the LABEL does not mean collapsing the STATE. The label answers
 * "what will happen"; the checkmark answers "what did you choose", and those
 * are different questions with different answers for a stranded user:
 *
 * | {@link LocaleSelection} | trigger label | marked row |
 * | ----------------------- | ------------- | ---------- |
 * | `offered`               | its own label | itself     |
 * | `stranded` (`ja_JP`)    | resolved label ("English") | none |
 * | `unresolved`            | the control's name | none  |
 *
 * A stranded user is told, in the trigger's accessible name, that their saved
 * language is no longer offered — so "English" reads as what the app is doing,
 * not as a choice they made. And an unresolved selection is NOT defaulted to
 * English: with nothing stored there is nothing to resolve, so the control
 * names itself and marks nothing.
 * @module components/molecules/LanguageSelector/LanguageSelectorContainer
 */
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import LanguageSelectorView, {
  SAVE_DEFAULT_LANGUAGE_KEYS,
  SAVE_DEFAULT_LANGUAGE_WARNING_KEY,
  isSaveDefaultPressable,
  type LanguageSelectorRow,
  type LanguageSelectorSaveDefault,
  type LanguageSelectorSaveDefaultRow,
  type LanguageSelectorSize,
  type LanguageSelectorTrigger,
} from "./LanguageSelectorView";

import {
  SUPPORTED_UI_LOCALE_OPTIONS,
  classifyLocaleSelection,
  getLanguageLabel,
  type LocaleOption,
  type LocaleSelection,
  type UiLocale,
} from "@/constants/locales";
import useMobileScreen from "@/hooks/useMobileScreen";

import type { TFunction } from "i18next";

/**
 * Catalog key for the control when no locale has been resolved. Deliberately
 * the name of the control rather than a language, so an unresolved selection
 * cannot be mistaken for a chosen one.
 */
export const LANGUAGE_CONTROL_LABEL_KEY = "language.control";

/** Catalog key for the heading rendered above the option list. */
export const LANGUAGE_SELECTOR_TITLE_KEY = "language.selectTitle";

/** Public props for the LanguageSelector molecule. */
export interface LanguageSelectorProps {
  /**
   * The currently selected locale as STORED — a raw `languagePreference`, a
   * locale outside the offered six, `null` for "none / original", or
   * `undefined` for "not resolved yet". The molecule classifies it; callers
   * never pre-resolve it and never pass a pre-computed label.
   */
  readonly selectedLocale?: string | null;
  /** Fires with the chosen offered locale. The surface closes either way. */
  readonly onSelectLocale: (locale: UiLocale) => void;
  /** The offered locales. Defaults to the canonical six. */
  readonly options?: readonly LocaleOption[];
  /** Which trigger to render (`"none"` for caller-owned affordances). */
  readonly trigger?: LanguageSelectorTrigger;
  /** Trigger size. */
  readonly size?: LanguageSelectorSize;
  /** Whether the trigger is inert. */
  readonly disabled?: boolean;
  /** Controlled open state. Omit for an uncontrolled trigger. */
  readonly isOpen?: boolean;
  /** Called when the surface asks to open (controlled mode). */
  readonly onOpen?: () => void;
  /** Called when the surface asks to close. */
  readonly onClose?: () => void;
  /** Optional trigger testID override, preserving consumer e2e contracts. */
  readonly triggerTestID?: string;
  /**
   * Optional trailing row promoting the current selection to the account
   * default (SE-6511).
   *
   * A PROP rather than a fourth control, per [LPW-1.6]: the choice "make this
   * my default" belongs beside the choice it promotes, and a separate control
   * elsewhere would make a per-summary override and an account setting look
   * like two unrelated things. Surfaces with no account default — the scout
   * report's translation sheet — simply omit it and render nothing.
   */
  readonly saveAsDefault?: LanguageSelectorSaveDefault;
}

/**
 * Derives the trigger's visible label from the selection state.
 *
 * The LANGUAGE NAMES themselves stay `getLanguageLabel` output rather than
 * catalog entries: a language is named in its own tongue ("Deutsch", not
 * "German"), which is the one label rule SE-6508 established and is why a
 * picker is readable to someone who cannot read the current UI locale. Only
 * the surrounding chrome is translated.
 * @param t - The catalog translate function
 * @param selection - The classified selection
 * @returns Copy that is never a raw locale code
 */
function triggerLabelFor(t: TFunction, selection: LocaleSelection): string {
  if (selection.status === "unresolved") return t(LANGUAGE_CONTROL_LABEL_KEY);
  if (selection.status === "offered") return getLanguageLabel(selection.locale);
  return getLanguageLabel(selection.code);
}

/**
 * Derives the trigger's accessible name, which is where the stranded case is
 * stated outright rather than being silently smoothed over.
 * @param t - The catalog translate function
 * @param selection - The classified selection
 * @returns The accessible name for the trigger
 */
function triggerAccessibilityLabelFor(
  t: TFunction,
  selection: LocaleSelection
): string {
  if (selection.status === "unresolved") return t("language.change");
  if (selection.status === "offered") {
    return t("language.changeCurrent", {
      language: getLanguageLabel(selection.locale),
    });
  }
  return t("language.changeStranded", {
    code: selection.code,
    language: getLanguageLabel(selection.code),
  });
}

/**
 * The one language picker: trigger plus option surface, sheet on small screens
 * and dialog on large ones.
 * @param props - See {@link LanguageSelectorProps}
 * @param props.selectedLocale - The stored locale, unresolved or stranded included
 * @param props.onSelectLocale - Selection callback
 * @param props.options - Offered locales (defaults to the canonical six)
 * @param props.trigger - Which trigger to render
 * @param props.size - Trigger size
 * @param props.disabled - Whether the trigger is inert
 * @param props.isOpen - Controlled open state
 * @param props.onOpen - Open request callback
 * @param props.onClose - Close request callback
 * @param props.triggerTestID - Optional trigger testID override
 * @param props.saveAsDefault - Optional save-as-default row and its state
 * @returns The language selector element
 */
const LanguageSelectorContainer = ({
  selectedLocale,
  onSelectLocale,
  options = SUPPORTED_UI_LOCALE_OPTIONS,
  trigger = "language-label",
  size = "md",
  disabled = false,
  isOpen,
  onOpen,
  onClose,
  triggerTestID,
  saveAsDefault,
}: LanguageSelectorProps): React.ReactElement => {
  const { t } = useTranslation();
  const isSmallScreen = useMobileScreen();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = isOpen !== undefined;
  const resolvedIsOpen = isControlled ? isOpen : internalIsOpen;

  const selection = useMemo(
    () => classifyLocaleSelection(selectedLocale),
    [selectedLocale]
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    if (!isControlled) setInternalIsOpen(true);
    onOpen?.();
  }, [disabled, isControlled, onOpen]);

  const handleClose = useCallback(() => {
    if (!isControlled) setInternalIsOpen(false);
    onClose?.();
  }, [isControlled, onClose]);

  const handleSelect = useCallback(
    (locale: UiLocale) => {
      if (!isControlled) setInternalIsOpen(false);
      onSelectLocale(locale);
      onClose?.();
    },
    [isControlled, onClose, onSelectLocale]
  );

  /*
   * Rows are built here, each with its handler already bound, so the view can
   * map them without allocating a closure per locale per render — the same
   * shape `DesktopDropdownMenu` uses. `isSelected` is where the three states
   * become visible: only an `offered` selection marks a row.
   */
  const rows = useMemo<readonly LanguageSelectorRow[]>(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label,
        isSelected:
          selection.status === "offered" && selection.locale === option.value,
        onPress: () => handleSelect(option.value),
      })),
    [handleSelect, options, selection]
  );

  /*
   * The save row's copy is resolved here, once, so the view stays presentation
   * only and both pickers that carry this affordance read the SAME catalog
   * keys (`SAVE_DEFAULT_LANGUAGE_KEYS`). An `accessibilityLabel` on a Pressable
   * REPLACES its children for assistive tech, so the failure message rendered
   * inside the row would otherwise be announced to nobody — a sighted user
   * would be told why the save failed and a screen reader user would not. It is
   * folded into the accessible name instead.
   */
  const saveAsDefaultRow = useMemo<
    LanguageSelectorSaveDefaultRow | undefined
  >(() => {
    if (!saveAsDefault) return undefined;
    const label = t(SAVE_DEFAULT_LANGUAGE_KEYS[saveAsDefault.status]);
    const accessibleName =
      saveAsDefault.status === "error" && saveAsDefault.errorMessage
        ? t("language.saveDefault.errorAccessible", {
            label,
            reason: saveAsDefault.errorMessage,
          })
        : label;
    /*
     * Only while the offer is LIVE. `saved` and `already-default` have nothing
     * to warn about — the language IS the default — and `saving` is a moment,
     * not a decision. That is the same set `isSaveDefaultPressable` names, so
     * the warning appears exactly when pressing the row would still change
     * something.
     */
    const warning = isSaveDefaultPressable(saveAsDefault.status)
      ? t(SAVE_DEFAULT_LANGUAGE_WARNING_KEY)
      : undefined;
    return { ...saveAsDefault, label, accessibleName, warning };
  }, [saveAsDefault, t]);

  return (
    <LanguageSelectorView
      rows={rows}
      triggerLabel={triggerLabelFor(t, selection)}
      triggerAccessibilityLabel={triggerAccessibilityLabelFor(t, selection)}
      title={t(LANGUAGE_SELECTOR_TITLE_KEY)}
      isOpen={resolvedIsOpen}
      isSmallScreen={isSmallScreen}
      trigger={trigger}
      size={size}
      disabled={disabled}
      onOpen={handleOpen}
      onClose={handleClose}
      triggerTestID={triggerTestID}
      saveAsDefault={saveAsDefaultRow}
    />
  );
};

LanguageSelectorContainer.displayName = "LanguageSelectorContainer";

export default LanguageSelectorContainer;
